/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICountryPickerViewProps } from './country-picker-all';

/**
 * Functional component which renders country picker module.
 * @param props - Configuration for the country picker component.
 * @returns React.FC<ICountryPickerViewProps> - Functional component of the country picker module.
 */
export const countryPickerView: React.FC<ICountryPickerViewProps> = (props: ICountryPickerViewProps) => {
    const {
        isModalOpen,
        modalContainer,
        modalHeaderContainer,
        modalBodyContainer,
        heading,
        modalFooterContainer,
        subheading,
        link,
        countryList,
        resources
    } = props;
    const preferredSite = props.context.request.cookies.get('_msdyn365___site_').value as string;

    function stripLocale(urlExt: string): string {
        const stripRegex = /^\/(eu-en|en|fr-ch|fr|eu)\/?/gi;
        const newUrlExt = urlExt.replace(stripRegex, '');
        return newUrlExt;
    }

    function prefhref(preferredSite: string, urlExt: string): string {
        return preferredSite.charAt(preferredSite.length - 1) === '/' || urlExt.charAt(0) === '/'
            ? `${preferredSite}${urlExt}`
            : `${preferredSite}/${urlExt}`;
    }
    const urlExt = stripLocale(props.context.request.url.requestUrl.pathname);

    if (preferredSite && countryList && isModalOpen) {
        const preferredSiteName = props.context.request.cookies.get('_msdyn365___sitename_').value as string;
        const currentLocaleName = function() {
            const currentLocale = props.context.request.url.requestUrl.pathname;

            if (currentLocale.indexOf('/eu-en') === 0) {
                return 'European Union (English)';
            } else if (currentLocale.indexOf('/en') === 0) {
                return 'Switzerland (English)';
            } else if (currentLocale.indexOf('/fr-ch') === 0) {
                return 'Suisse (Français)';
            } else if (currentLocale.indexOf('/fr') === 0) {
                return 'Union Européenne (Français)';
            } else if (currentLocale.indexOf('/eu') === 0) {
                return 'Europäische Union (Deutsch)';
            } else {
                return 'Schweiz (Deutsch)';
            }
        };
        return (
            <Module {...modalContainer}>
                <Node {...modalHeaderContainer}>
                    <h5 className='msc-modal__title'>
                        <h1 className='ms-country-picker__heading'>Welcome Back!</h1>
                        <div className='ms-country-picker__subheading'>
                            <p>
                                {resources.geolocationOopsText}
                                <b className='oopsLink'>{` ${currentLocaleName()}`}</b>.
                            </p>
                        </div>
                    </h5>
                </Node>
                <Node {...modalBodyContainer}>
                    <a href={prefhref(preferredSite, urlExt)}>
                        <h3>
                            {resources.geolocationReturnText}
                            {preferredSiteName}?
                        </h3>
                    </a>
                </Node>
                <Node {...modalBodyContainer}>—{resources.geolocationOrText}—</Node>
                <Node {...modalBodyContainer}>
                    <h3 className='pickNew'>{resources.geolocationPickNewText}</h3>
                </Node>
                <Node {...modalBodyContainer}>
                    {countryList.map(country => (
                        <div className='pickerGroup' key={country.name}>
                            {country?.elements}
                        </div>
                    ))}
                </Node>

                <Node {...modalFooterContainer}>{link}</Node>
            </Module>
        );
    }

    return isModalOpen && countryList ? (
        <Module {...modalContainer}>
            <Node {...modalHeaderContainer}>
                {heading}
                <div className='ms-country-picker__subheading'>{resources.geolocationSubText}</div>
                {subheading}
            </Node>
            <Node {...modalBodyContainer}>
                {countryList.map(country => (
                    <div className='pickergroup' key={country.name}>
                        {country?.elements}
                    </div>
                ))}
            </Node>

            <Node {...modalFooterContainer}>{link}</Node>
        </Module>
    ) : null;
};

export default countryPickerView;
